import { type TFunction } from 'i18next';

export interface RoomCriteriaData {
  id: string;
  roomName: string;
  roomCategory: string;
  luxLevel: number;
  airRatePerArea: number;
  airRatePerPerson: number;
  luminairesRequired: boolean;
  sprinklersRequired: boolean;
  smokeDetectorsRequired: boolean;
  pirDetectorsRequired: boolean;
  extractOnly: boolean;
  supplyOnly: boolean;
  minimumExtractRate: number;
  nrRating: number;
  occupancyRate: number;
  lightingLoad: number;
  smallPowerLoad: number;
  useOccupancyRate: boolean;
  useAirChangeRate: boolean;
  airChangeRate: number;
  sprinklerHighHazard: boolean;
  // Track which values are from specifications vs defaults
  valueSource?: Partial<{
    [K in Exclude<keyof RoomCriteriaData, 'id' | 'valueSource'>]:
      | 'specification'
      | 'default'
      | 'user';
  }>;
}

export interface ColumnConfig {
  key: Exclude<keyof RoomCriteriaData, 'id' | 'valueSource'>;
  label: string;
  visible: boolean;
  editable: boolean;
  type: 'text' | 'number' | 'checkbox';
  unit?: string;
}

export const getColumnConfigs = (t: TFunction): ColumnConfig[] => [
  {
    key: 'roomName',
    label: t('room-criteria.columns.room-name'),
    visible: true,
    editable: false,
    type: 'text',
  },
  {
    key: 'roomCategory',
    label: t('room-criteria.columns.room-category'),
    visible: true,
    editable: false,
    type: 'text',
  },
  {
    key: 'luxLevel',
    label: t('room-criteria.columns.lux-level'),
    visible: true,
    editable: true,
    type: 'number',
    unit: 'lux',
  },
  {
    key: 'airRatePerArea',
    label: t('room-criteria.columns.air-rate-per-area'),
    visible: true,
    editable: true,
    type: 'number',
    unit: 'l/s/m²',
  },
  {
    key: 'airRatePerPerson',
    label: t('room-criteria.columns.air-rate-per-person'),
    visible: true,
    editable: true,
    type: 'number',
    unit: 'l/s/person',
  },
  {
    key: 'luminairesRequired',
    label: t('room-criteria.columns.luminaires-required'),
    visible: true,
    editable: true,
    type: 'checkbox',
  },
  {
    key: 'sprinklersRequired',
    label: t('room-criteria.columns.sprinklers-required'),
    visible: true,
    editable: true,
    type: 'checkbox',
  },
  {
    key: 'smokeDetectorsRequired',
    label: t('room-criteria.columns.smoke-detectors-required'),
    visible: true,
    editable: true,
    type: 'checkbox',
  },
  {
    key: 'pirDetectorsRequired',
    label: t('room-criteria.columns.pir-detectors-required'),
    visible: true,
    editable: true,
    type: 'checkbox',
  },
  {
    key: 'extractOnly',
    label: t('room-criteria.columns.extract-only'),
    visible: true,
    editable: true,
    type: 'checkbox',
  },
  {
    key: 'supplyOnly',
    label: t('room-criteria.columns.supply-only'),
    visible: true,
    editable: true,
    type: 'checkbox',
  },
  {
    key: 'minimumExtractRate',
    label: t('room-criteria.columns.minimum-extract-rate'),
    visible: true,
    editable: true,
    type: 'number',
    unit: 'l/s',
  },
  {
    key: 'nrRating',
    label: t('room-criteria.columns.nr-rating'),
    visible: true,
    editable: true,
    type: 'number',
    unit: 'NR',
  },
  {
    key: 'occupancyRate',
    label: t('room-criteria.columns.occupancy-rate'),
    visible: true,
    editable: true,
    type: 'number',
    unit: 'm²/person',
  },
  {
    key: 'lightingLoad',
    label: t('room-criteria.columns.lighting-load'),
    visible: true,
    editable: true,
    type: 'number',
    unit: 'W/m²',
  },
  {
    key: 'smallPowerLoad',
    label: t('room-criteria.columns.small-power-load'),
    visible: true,
    editable: true,
    type: 'number',
    unit: 'W/m²',
  },
  {
    key: 'useOccupancyRate',
    label: t('room-criteria.columns.use-occupancy-rate'),
    visible: false,
    editable: true,
    type: 'checkbox',
  },
  {
    key: 'useAirChangeRate',
    label: t('room-criteria.columns.use-air-change-rate'),
    visible: false,
    editable: true,
    type: 'checkbox',
  },
  {
    key: 'airChangeRate',
    label: t('room-criteria.columns.air-change-rate'),
    visible: true,
    editable: true,
    type: 'number',
    unit: '1/h',
  },
  {
    key: 'sprinklerHighHazard',
    label: t('room-criteria.columns.sprinkler-high-hazard'),
    visible: false,
    editable: true,
    type: 'checkbox',
  },
];
// Constants for column management
export const ALWAYS_VISIBLE_COLUMNS = ['roomName', 'roomCategory'] as const;
